///
/// EarthBrowser
///
/// Created by Matt Giger on 11/18/2023
/// Copyright (c) 2023 Geodata Labs
///

import { range } from "@stovokor/math/range"
import { vec2 } from "@stovokor/math/vector"

export interface segment {
	extract(from: vec2, resolution: number): vec2[]
}

export class path2d {

	protected segments: segment[] = []

	public close(): path2d {
		this.segments.push(new close())
		return this
	}

	public move(x: number, y: number): path2d {
		this.segments.push(new move(x, y))
		return this
	}

	public line(x: number, y: number): path2d {
		this.segments.push(new line(x, y))
		return this
	}

	public cubic(x1: number, y1: number, x2: number, y2: number, x: number, y: number): path2d {
		this.segments.push(new bezier(x1, y1, x2, y2, x, y))
		return this
	}

	public quadratic(x1: number, y1: number, x: number, y: number): path2d {
		this.segments.push(new quadratic(x1, y1, x, y))
		return this
	}

	public spline(x1: number, y1: number, x: number, y: number): path2d {
		this.segments.push(new spline(x1, y1, x, y))
		return this
	}

	public cardinal(domain: number[], range: number[]): path2d {
		this.segments.push(new CardinalSpline(domain, range))
		return this
	}

	// Turn the path components into a set of polygon points and hole points
	public extract(resolution: number = 6): vec2[][] {
		let last = new vec2(0, 0)
		const paths: vec2[][] = []
		let currentPath: vec2[] = []
		for(const segment of this.segments) {
			if(segment instanceof close) {
				if(currentPath.length > 0) {
					paths.push(this.deduplicated(currentPath))
					currentPath = []
				}
			} else if(segment instanceof move) {
				last = segment.point
			} else {
				const points = segment.extract(last, resolution)
				last = points[points.length - 1]
				currentPath.push(...points)
			}
		}
		if(currentPath.length > 0) {
			paths.push(this.deduplicated(currentPath))
		}
		return paths
	}

	private deduplicated(points: vec2[]): vec2[] {
		const deduped: vec2[] = []
		const epsilon = 0.001 // Minimum distance threshold to avoid poly2tri issues
		let last = new vec2(0, 0)
		points.forEach(point => {
			const dx = point.x - last.x
			const dy = point.y - last.y
			const distSq = dx * dx + dy * dy
			if(distSq > epsilon * epsilon) {
				deduped.push(point)
				last = point
			}
		})
		return deduped
	}
}

class close implements segment {
	public extract(_from: vec2, _resolution: number): vec2[] { return [] }
}

class move implements segment {
	public point: vec2

	constructor(x: number, y: number) { this.point = new vec2(x, y) }

	public extract(_from: vec2, _resolution: number): vec2[] {
		return [this.point]
	}
}

class line implements segment {
	public point: vec2

	constructor(x: number, y: number) { this.point = new vec2(x, y) }

	public extract(from: vec2, _resolution: number): vec2[] {
		return [from, this.point]
	}
}

class bezier implements segment {
	protected x1: number
	protected y1: number
	protected x2: number
	protected y2: number
	protected x: number
	protected y: number

	constructor(x1: number, y1: number, x2: number, y2: number, x: number, y: number) {
		this.x1 = x1
		this.y1 = y1
		this.x2 = x2
		this.y2 = y2
		this.x = x
		this.y = y
	}

	public extract(from: vec2, resolution: number): vec2[] {
		const steps = Array.from({ length: resolution }).map((_e, i): number => i / resolution)
		return steps.map(t => {
			const k = 1 - t
			return new vec2(
				k*k*k*from.x + 3*k*k*t*this.x1 + 3*k*t*t*this.x2 + t*t*t*this.x,
				k*k*k*from.y + 3*k*k*t*this.y1 + 3*k*t*t*this.y2 + t*t*t*this.y
			)

		})
	}
}

class quadratic implements segment {
	public x1: number
	public y1: number
	public x: number
	public y: number

	constructor(x1: number, y1: number, x: number, y: number) {
		this.x1 = x1
		this.y1 = y1
		this.x = x
		this.y = y
	}

	public extract(from: vec2, resolution: number): vec2[] {
		const steps = Array.from({ length: resolution }).map((_e, i): number => i / resolution)
		return steps.map(t => {
			const k = 1 - t
			return new vec2(
				k*k*from.x + 2*k*t*this.x1 + t*t*this.x,
				k*k*from.y + 2*k*t*this.y1 + t*t*this.y
			)
		})
	}
}

class spline implements segment {
	public x1: number
	public y1: number
	public x: number
	public y: number

	constructor(x1: number, y1: number, x: number, y: number) {
		this.x1 = x1
		this.y1 = y1
		this.x = x
		this.y = y
	}

	public extract(from: vec2, resolution: number): vec2[] {
		const steps = Array.from({ length: resolution }).map((_e, i): number => i / resolution)
		return steps.map(t => {
			const k = 1 - t
			return new vec2(
				k*from.x + 2*t*this.x1 + t*t*this.x,
				k*from.y + 2*t*this.y1 + t*t*this.y
			)
		})
	}
}

class CardinalSpline implements segment {
	private domainValues: number[]
	private rangeValues: number[]
	private tension: number
	public results: vec2[]
	public xrange = new range(0, 0)
	public yrange = new range(0, 0)

	constructor(domainValues: number[], rangeValues: number[], resolution: number = 6, tension: number = 0.5) {
		this.domainValues = [domainValues[0], ...domainValues, domainValues[domainValues.length - 1]]
		this.rangeValues = [rangeValues[0], ...rangeValues, rangeValues[rangeValues.length - 1]]
		this.tension = tension
		this.results = this.interpolated(resolution)
	}

	public extract(_from: vec2, resolution: number): vec2[] {
		return this.interpolated(resolution)
	}

	public interpolated(resolution: number): vec2[] {
		const tension = this.tension
		const domainVals = this.domainValues
		const rangeVals = this.rangeValues

		this.results = []
		const results = this.results

		let miny = Number.POSITIVE_INFINITY
		let maxy = Number.NEGATIVE_INFINITY

		for(let i=1; i<domainVals.length-2; i++) {
			for(let t=0; t<resolution; t++) {
				// calculate tension vectors
				const t1x = (domainVals[i+1] - domainVals[i-1]) * tension
				const t2x = (domainVals[i+2] - domainVals[i]) * tension
				const t1y = (rangeVals[i+1] - rangeVals[i-1]) * tension
				const t2y = (rangeVals[i+2] - rangeVals[i]) * tension

				const step = t / resolution
				const st2 = step * step
				const st3 = st2 * step

				// calculate the cardinal points
				const c1 = 2 * st3 - 3 * st2 + 1
				const c2 = -2 * st3 + 3 * st2
				const c3 = st3 - 2 * st2 + step
				const c4 = st3 - st2

				// calculate x and y cords with common control vectors
				const x = c1 * domainVals[i] + c2 * domainVals[i+1] + c3 * t1x + c4 * t2x
				const y = c1 * rangeVals[i] + c2 * rangeVals[i+1] + c3 * t1y + c4 * t2y

				if(y < miny) {
					miny = y
				}
				if(y > maxy) {
					maxy = y
				}

				results.push(new vec2(x, y))
			}
		}

		this.xrange = new range(domainVals[0], domainVals[domainVals.length - 1])
		this.yrange = new range(miny, maxy)

		return results
	}

	public interpolatedRangeValue(domainValue: number): number {
		if(domainValue <= this.xrange.min) {
			return this.rangeValues[0]
		} else if(domainValue >= this.xrange.max) {
			return this.rangeValues[this.rangeValues.length - 1]
		}

		const tension = this.tension
		const domainVals = this.domainValues
		const rangeVals = this.rangeValues

		for(let i=1; i<domainVals.length-2; i++) {
			if(domainValue >= domainVals[i] && domainValue <= domainVals[i+1]) {
				// calculate tension vectors
				const t1y = (rangeVals[i+1] - rangeVals[i-1]) * tension
				const t2y = (rangeVals[i+2] - rangeVals[i]) * tension

				const t = (domainValue - domainVals[i]) / (domainVals[i+1] - domainVals[i])
				const step = t
				const st2 = step * step
				const st3 = st2 * step

				// calculate the cardinal points
				const c1 = 2 * st3 - 3 * st2 + 1
				const c2 = -2 * st3 + 3 * st2
				const c3 = st3 - 2 * st2 + step
				const c4 = st3 - st2

				// calculate y coord with common control vectors
				const y = c1 * rangeVals[i] + c2 * rangeVals[i+1] + c3 * t1y + c4 * t2y

				return y
			}
		}

		return 0
	}
}